import type { AnyObject } from "antd/es/_util/type";
import type { AxiosError, AxiosResponse } from "axios";
import type { ErrorInner } from "../app/generated-axios-client";
import type { Err } from "../types/err";

const AVATAR_COLORS = [
  "#1677ff",
  "#52c41a",
  "#fa8c16",
  "#722ed1",
  "#eb2f96",
  "#13c2c2",
  "#faad14",
  "#f5222d",
];

export function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function buildSelectOptions<T>(
  items: T[],
  getValue: (item: T) => string | number,
  getLabel: (item: T) => string | number | React.ReactNode,
  getSearchItem?: (item: T) => string | number,
  getKey?: (item: T) => string | number
): { key: string; value: string; label: string | React.ReactNode; searchText: string }[] {
  return (items || []).map((item) => ({
    value: String(getValue(item)),
    label: getLabel(item),
    searchText: String(getSearchItem ? getSearchItem(item) : getLabel(item)),
    key: String(getKey ? getKey(item) : getValue(item)),
  }));
}

export const sanitizeObjectValues = (objectData: AnyObject): Record<string, any> => {
  return Object.entries(objectData).reduce<Record<string, any>>(
    (accumulated, [fieldName, fieldValue]) => {
      if (Array.isArray(fieldValue)) {
        accumulated[fieldName] = fieldValue;
      } else if (fieldValue != null) {
        const trimmed = String(fieldValue).trim();
        if (trimmed) {
          accumulated[fieldName] = trimmed;
        }
      }
      return accumulated;
    },
    {}
  );
};

export const sanitizedFilter = (filter: AnyObject) => {
  const sanitizedFilter = { ...filter };
  if (sanitizedFilter.date_from?.format) {
    sanitizedFilter.date_from = sanitizedFilter.date_from.format("YYYY-MM-DD");
  }
  if (sanitizedFilter.date_to?.format) {
    sanitizedFilter.date_to = sanitizedFilter.date_to.format("YYYY-MM-DD");
  }

  return sanitizeObjectValues(sanitizedFilter);
};

export function arrayToObject<T extends Record<string, any>>(
  array: Array<T>,
  keyField: string
): Record<string, T> {
  return array.reduce<Record<string, T>>(
    (obj, item) => {
      if (keyField in item) {
        obj[item[keyField]] = item;
      }
      return obj;
    },
    {} as Record<string, T>
  );
}

export function isEmpty(value: string | null | undefined) {
  return (
    value == null ||
    (typeof value === "string" && value.trim().length === 0) ||
    typeof value == "undefined"
  );
}

export function filterDataForDispatch(
  changedValues: Record<string, any>
): Record<string, string> {
  const filters = new Set(["turbine", "client", "items", "location"]);
  const payload = {} as Record<string, string>;

  for (const [key, value] of Object.entries(changedValues)) {
    if (filters.has(key)) {
      payload[key] = value;
    }
  }

  return payload;
}

export function responseUnwrap<DataType>(
  response: AxiosResponse<DataType> | AxiosError<ErrorInner>
): DataType | Err {
  if ("isAxiosError" in response) {
    return {
      error: response.message,
      status: response.status,
      data: response.response?.data,
    };
  } else {
    return response.data;
  }
}

// The ticket_rejections / ticket_delays endpoints store one notification row per
// recipient (creator + each assignee + each coordinator), so a single event surfaces
// as several near-identical rows. Collapse them for list rendering by keying on
// sender, message and the minute of creation. The sender id has to stay as wide as the
// generated types (where it is a number): narrowing it makes Notification fail the
// constraint, which collapses T to the constraint itself and strips every other field
// from the rows the caller gets back.
export function dedupeNotificationsByContent<
  T extends {
    message?: string | null;
    created_at?: string | null;
    sender?: { id?: string | number } | null;
  },
>(notifications: T[]): T[] {
  const seen = new Set<string>();
  const deduped: T[] = [];
  for (const notification of notifications) {
    const key = `${notification.sender?.id ?? ""}|${notification.message ?? ""}|${notification.created_at?.slice(0, 16) ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(notification);
  }
  return deduped;
}

// Resolves a set of referenced entity ids to display names, reusing whatever's
// already in `seed` (e.g. the current ticket's nested objects) and only fetching
// ids missing from it. A failed lookup (e.g. a since-deleted entity) is skipped,
// not thrown — the id-less entries just stay unresolved.
export async function resolveMissingEntityMap<T extends { id?: string }>(
  ids: string[],
  seed: Record<string, string>,
  fetchById: (id: string) => Promise<AxiosResponse<T> | AxiosError<ErrorInner>>,
  getName: (entity: T) => string
): Promise<Record<string, string>> {
  const missingIds = ids.filter((id) => !(id in seed));
  if (missingIds.length === 0) return seed;

  const responses = await Promise.all(missingIds.map((id) => fetchById(id)));
  const map = { ...seed };
  for (const response of responses) {
    const unwrapped = responseUnwrap<T>(response);
    if (!("error" in unwrapped) && unwrapped.id) {
      map[unwrapped.id] = getName(unwrapped);
    }
  }
  return map;
}
